import type { ReadableSpan, Span, SpanProcessor } from '@opentelemetry/sdk-trace-base';

/**
 * XHR lifecycle markers with no diagnostic value.
 *
 * `open` and `send` fire microseconds apart at the start of every request, and
 * `loaded` fires at the end — all three are already implied by the span's own
 * start and end timestamps and its `http.response.status_code`.
 *
 * Deliberately NOT dropped: `error`, `timeout` and `abort`. They look like the
 * same class of marker but they are the only record of *why* a request ended
 * early — `abort` in particular sets no error status on the span, so dropping
 * it would make a cancelled request indistinguishable from a successful one.
 * A deny-list rather than an allow-list, so anything OTel adds later survives
 * by default instead of being silently swallowed.
 */
const NOISE_EVENT_NAMES = new Set(['open', 'send', 'loaded']);

/**
 * Strip lifecycle-marker events from browser client spans.
 *
 * Registered before Logfire's exporting processor, so mutating in `onEnd` is
 * visible to the exporter — the same pattern `RedactingSpanProcessor` uses on
 * the server.
 *
 * `events` is a readonly property holding a mutable array, so this splices in
 * place rather than reassigning.
 */
export class PruneClientSpanEventsProcessor implements SpanProcessor {
  onStart(_span: Span): void {}

  onEnd(span: ReadableSpan): void {
    const { events } = span;
    if (!Array.isArray(events) || events.length === 0) return;

    for (let i = events.length - 1; i >= 0; i--) {
      if (NOISE_EVENT_NAMES.has(events[i]?.name)) events.splice(i, 1);
    }
  }

  async forceFlush(): Promise<void> {}

  async shutdown(): Promise<void> {}
}
